import axios from "axios";
import { isEmpty, omit, omitBy, isNil } from "lodash";
import useSession from "Hooks/session.hook";
import errorHandler from "Utils/error-handler";
import { getAccessToken, getProfileID } from "Helpers/session";
import { DefaultFilter } from "Types/common.types";
import { string } from "yup";
import normalize from "Utils/normalizer";
import { FullscreenConfigEnums, SortingLinkTypePrefix } from "Data/settings";
import { apiURL } from "Helpers/index";
import { v4 as uuidv4 } from 'uuid';

const baseUrl = `${process.env.API_URL}/${process.env.API_VER}`;
const sseBearerToken = process.env.SSE_BEARER_TOKEN

type ConfigListFilters = {
  type?: FullscreenConfigEnums;
  companyOrgID: string;
};

// list config list
export const fetchTrackingConfigList = async (
  filters: ConfigListFilters,
  pagination?,
  sorting?,
) => {
  const accessToken = getAccessToken();
  const url = `${baseUrl}/sorting_link/list`;

  try {
    const response: any = await axios({
      method: "POST",
      url: url,
      data: {
        filters: omitBy(
          {
            type: filters.type,
            companyOrgID: filters.companyOrgID,
          },
          isNil,
        ),
        pagination: pagination,
        sorting: sorting,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error
      ? response.data.sortingLinks.map((s) => normalize.response.fullscreen_config(s))
      : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrackingConfigList(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// fetch config
export const fetchTrackingConfig = async (sortingLinkID: SortingLinkTypePrefix) => {
  const accessToken = getAccessToken();
  const url = `${baseUrl}/sorting_link/fetch`;

  try {
    const response: any = await axios({
      method: "POST",
      url: url,
      data: {
        sortingLinkID: sortingLinkID,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error
      ? normalize.response.fullscreen_config(response.data.sortingLink)
      : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrackingConfig(sortingLinkID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// create config
export const createTrackingConfig = async (payload) => {
  const accessToken = getAccessToken();
  const url = `${baseUrl}/sorting_link/create`;

  try {
    const response: any = await axios({
      method: "POST",
      url: url,
      data: normalize.model.fullscreen_config(payload),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error ? response.data : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createTrackingConfig(payload);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// create config
export const updateTrackingConfig = async (payload) => {
  const accessToken = getAccessToken();
  const url = `${baseUrl}/sorting_link/update`;

  try {
    const response: any = await axios({
      method: "POST",
      url: url,
      data: {
        sortingLink: normalize.model.fullscreen_config(payload),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error ? response.data : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateTrackingConfig(payload);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// delete config
export const deleteTrackingConfig = async (sortingLinkID: SortingLinkTypePrefix) => {
  const accessToken = getAccessToken();
  const url = `${baseUrl}/sorting_link/delete`;

  try {
    const response: any = await axios({
      method: "POST",
      url: url,
      data: { sortingLinkID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error ? response.data : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await deleteTrackingConfig(sortingLinkID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

//   Public
// =============================================
// list public box list
export const fetchPublicSlotDataList = async (sortingLinkID) => {
  let url = `${baseUrl}/sorting_link/public/rack/slot_data/list`;

  // append the sortingLinkID to the url
  url = sortingLinkID ? url + "?publicSortingLinkID=" + sortingLinkID : url;

  try {
    const response: any = await axios({
      method: "GET",
      url: url,
      data: {
        sortingLinkID: sortingLinkID,
      },
    });

    return !response.error
      ? response.data.racks.map((r) => normalize.response.public_fullscreen(r))
      : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(error);

    if (!isEmpty(error)) {
      const callback = async () => await fetchPublicSlotDataList(sortingLinkID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// list public trolley list
export const fetchPublicTrolleyList = async (sortingLinkID) => {
  let url = `${baseUrl}/sorting_link/public/trolley/list`;

  // append the sortingLinkID to the url
  url = sortingLinkID ? url + "?publicSortingLinkID=" + sortingLinkID : url;

  try {
    const response: any = await axios({
      method: "GET",
      url: url,
      data: {
        sortingLinkID: sortingLinkID,
      },
    });

    return !response.error ? response.data : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchPublicTrolleyList(sortingLinkID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchSSETicket = async (uniqueID: string = '') => {
  let url = new URL(apiURL('/sse/public_screen/ticket/create'))
  url.searchParams.append('uniqueID', uniqueID);

  try {
    const response: any = await axios({
      method: "GET",
      url: url.toString(),
      headers: { Authorization: `Bearer ${sseBearerToken}` },
    });
    
    return !response.error ? response.data : response;
  } catch (err: any) {
    return { error: { message: err.message } };
  }
}

export const fetchSSESubscription = async (isPublic = false) => {
  const url = new URL(apiURL("/sse/subscription/fetch"));
  const uuid = uuidv4()

  const getPublicParams = async () => {
    const { jwtData, publicScreenTicket } = await fetchSSETicket(uuid);
    return {
      profileID: jwtData.profileID,
      publicScreenTicket,
      uniqueID: uuid
    };
  };

  const getPrivateParams = () => ({
    profileID: getProfileID(),
    accessToken: getAccessToken(),
    uniqueID: uuid
  });

  const params = isPublic ? await getPublicParams() : getPrivateParams();

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  return new EventSource(url.toString());
};